import { useEffect, useState } from 'react';

import type { GpsPosition } from '@/types/geo';

export interface UseGpsResult {
  position: GpsPosition | null;
  /** User-facing message when the watch failed (permission denied, no provider). */
  error: string | null;
}

export function useGps(): UseGpsResult {
  const [position, setPosition] = useState<GpsPosition | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let gotFix = false;

    const apply = (pos: GeolocationPosition) => {
      if (cancelled) return;
      gotFix = true;
      setError(null);
      setPosition({
        lat: pos.coords.latitude,
        lon: pos.coords.longitude,
        speed: pos.coords.speed,
        heading: pos.coords.heading,
      });
    };

    // Fast first paint: grab a cached / coarse (network-based) fix immediately so the map can
    // centre right away, while the high-accuracy watch below warms up a precise GPS lock (a
    // cold lock can take many seconds). `!gotFix` guards against clobbering a watch fix that
    // already landed; the streaming watch then refines this within a second or two.
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!gotFix) apply(pos);
      },
      () => {}, // ignore — the watch's handler reports the real, persistent errors
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600_000 }
    );

    const watchId = navigator.geolocation.watchPosition(
      apply,
      (err) => {
        // TIMEOUT is transient — the watch keeps trying (tunnels, urban canyons);
        // surfacing it would just flash a banner mid-ride. The other two are worth
        // telling the user about, since the map silently never follows otherwise.
        if (err.code === err.PERMISSION_DENIED) {
          setError('Location access denied — enable it in your browser settings to use the map.');
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setError('Location unavailable — check that GPS is enabled.');
        }
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
    return () => {
      cancelled = true;
      navigator.geolocation.clearWatch(watchId);
    };
  }, []);

  return { position, error };
}
